import { useRef, useState } from "react";
import { LANG_LABELS, LANG_SHORT, PROOF_KIND_LABELS, TEXT_GROUP_LABELS, TRANSLATION_LIMITS, dishLangState, editsCatalog, editsMenus, keepsName, proofDiff, proofOf, proofreadEstimate, publishes, rowState, translateEstimate } from "@speisekarte/core";
import type { Dish, Lang, ProofKind, TextGroup, TextRow, TextStatus, TextTarget } from "@speisekarte/core";
import { api, messageOf, store, useStore } from "../store.ts";
import type { Data } from "../store.ts";
import { NetBehind, outdated } from "./Web.tsx";
import { JobLine, PhraseField, TargetRows, centsText, checked, reviewHref, targetLabel, textsCount, textsName, textsOn, toReview, useTextJobs, useTextStatus } from "./TextWork.tsx";
import { useModel } from "../model.ts";
import type { TextJobs } from "./TextWork.tsx";

type Filter = "pruefen" | "offen" | "alle";
const GROUPS: TextGroup[] = ["dish", "drink", "card", "category", "tag", "allergen", "text", "price", "footer"];
const shown = (s: string) => s.replace(/ /g, "␣");

/** Whether a row is in a filter for some of the targets. */
const inFilter = (r: TextRow, targets: readonly TextTarget[], f: Filter) =>
  targets.some((t) => {
    const st = rowState(r, t);
    return st !== undefined && (f === "alle" || (f === "pruefen" ? st === "review" || st === "stale" : st === "open"));
  });

/** The steps of „Fertig machen“ (S61 § 5): check the German, look through, translate, look through, done. */
type Step = 1 | 2 | 3 | 4 | 5;
function stepOf(status: TextStatus, jobs: TextJobs): Step {
  const de = status.counts.de;
  const langs = status.targets.filter((t): t is Lang => t !== "de");
  if (jobs.proofing || (de && de.open.dishes + de.open.texts > 0)) return 1;
  if (de && de.review.dishes + de.review.texts > 0) return 2;
  if (jobs.translating || langs.some((l) => (status.counts[l]?.open.dishes ?? 0) + (status.counts[l]?.open.texts ?? 0) > 0)) return 3;
  if (langs.some((l) => toReview(status.counts[l]) > 0)) return 4;
  return 5;
}
const STEP_LABELS: Record<Exclude<Step, 5>, string> = { 1: "Deutsch prüfen", 2: "Vorschläge durchsehen", 3: "Übersetzen", 4: "Übersetzungen durchsehen" };

/**
 * „Texte prüfen und übersetzen“ (S61): every text of the catalogue or of one card with its state for
 * each target – „Deutsch ✓“ (S47) and the languages (S36) – where each is looked through and changed:
 * a correction taken or refused, a translation typed or „Passt“. The jobs start here; „Fertig machen“
 * leads through them in order (the German first) to an end. Saved as each field is left.
 */
export function Review({ menuId, params }: { menuId: string; params: URLSearchParams }) {
  const s = useStore();
  const data = s.data!;
  const menu = menuId ? (data.menus.find((m) => m.id === menuId) ?? null) : null;
  const status = useTextStatus(data, menu);
  const jobs = useTextJobs(data.version);
  // Without a model no guided flow and no checking (S61 § 11): the page by hand, „Texte übersetzen“.
  const model = useModel();
  const name = textsName(data, model);
  const canJob = editsCatalog(s.role) && jobs.available && model;
  const langs = status.targets.filter((t): t is Lang => t !== "de");
  const hasDe = status.targets.includes("de");
  const asked = params.get("sprache");
  const first: TextTarget[] = asked && langs.includes(asked as Lang) ? [...(hasDe ? (["de"] as const) : []), asked as Lang] : status.targets;
  const [targets, setTargets] = useState<TextTarget[]>(first);
  // What there is to do in the targets shown first: to look through, else open, else all.
  const [filter, setFilter] = useState<Filter>(() => (params.get("filter") === "pruefen" ? "pruefen" : status.rows.some((r) => inFilter(r, first, "pruefen")) ? "pruefen" : status.rows.some((r) => inFilter(r, first, "offen")) ? "offen" : "alle"));
  const [guided, setFlow] = useState(params.get("ablauf") === "1");
  const flow = guided && model;
  const [panel, setPanel] = useState(false);
  const [q, setQ] = useState("");
  // Where the texts come from as filters too, as the list groups them (Martin, 02.11.2026).
  const [group, setGroup] = useState<TextGroup | null>(null);
  const back = menu ? (
    <a className="back" href={`#/speisekarte/${menu.id}?schritt=2`} data-testid="review-back">
      ← Zurück zur Karte
    </a>
  ) : (
    <a className="back" href="#/kataloge">
      ← Kataloge
    </a>
  );
  if (!textsOn(data)) {
    return (
      <div>
        <p>{back}</p>
        <section className="card">
          <h2>Texte prüfen</h2>
          <p>Erst unter Konto → Sprachen „Deutsch prüfen“ oder eine weitere Sprache einschalten.</p>
        </section>
      </div>
    );
  }
  if (menuId && !menu) {
    return (
      <div>
        <p>{back}</p>
        <p className="muted">Diese Speisekarte gibt es nicht mehr.</p>
      </div>
    );
  }

  const step = stepOf(status, jobs);
  // In the flow, the step decides what shows: the German alone, then the languages.
  const view: TextTarget[] = flow ? (step <= 2 ? (["de"] as TextTarget[]) : langs) : targets.filter((t) => status.targets.includes(t));
  const f: Filter = flow ? (step === 2 || step === 4 ? "pruefen" : "offen") : filter;
  // The flow shows all that is due in its step, never what a search of the expert mode left (Martin, 02.11.2026).
  const needle = flow ? "" : q.trim().toLowerCase();
  const searched = (r: TextRow, nd = needle) => !nd || (r.dish ? `${r.dish.number} ${r.dish.name} ${r.dish.description}` : r.text!).toLowerCase().includes(nd);
  const found = status.rows.filter((r) => inFilter(r, view, f) && searched(r));
  // Likewise the group, only in the expert mode.
  const g = flow ? null : group;
  const rows = g ? found.filter((r) => r.group === g) : found;
  const groups = GROUPS.map((x) => [x, found.filter((r) => r.group === x).length] as const).filter(([x, n]) => n > 0 || x === g);
  const count = (x: Filter) => status.rows.filter((r) => inFilter(r, view, x)).length;
  const deCounts = status.counts.de;
  const deOpen = deCounts ? deCounts.open.dishes + deCounts.open.texts : 0;
  const deReview = deCounts ? deCounts.review.dishes + deCounts.review.texts : 0;
  /**
   * The filters build on each other: what to show, its state, the group, the search (Martin,
   * 02.11.2026). A choice the person changed above empties one below: that one goes – the state to
   * the next with something in it (durchsehen, offen, alle), the group to all. Emptied by work done,
   * a filter stays and the list says so (below); the search, typed, is never taken away.
   */
  const settle = (ts: TextTarget[], fx: Filter) => {
    const shown = ts.filter((t) => status.targets.includes(t));
    const has = (x: Filter) => status.rows.some((r) => inFilter(r, shown, x));
    const nf = has(fx) ? fx : ((["pruefen", "offen", "alle"] as const).find(has) ?? "alle");
    if (nf !== filter) setFilter(nf);
    if (group && !status.rows.some((r) => r.group === group && inFilter(r, shown, nf) && searched(r))) setGroup(null);
  };
  const toggle = (t: TextTarget) => {
    const next = targets.includes(t) ? (targets.length > 1 ? targets.filter((x) => x !== t) : targets) : status.targets.filter((x) => x === t || targets.includes(x));
    setTargets(next);
    settle(next, filter);
  };
  const pick = (id: string) => (location.hash = reviewHref(id || null));
  const reviewable = langs.flatMap((l) => (view.includes(l) ? rows.filter((r) => r.langs[l] === "review").map((r) => ({ l, r })) : []));

  return (
    <div className="review">
      <p>{back}</p>
      <section className="card">
        <div className="review-head">
          <h2 data-testid="review-title">{name}</h2>
          {flow && step < 5 && (
            <button onClick={() => setFlow(false)} data-testid="flow-leave">
              Expertenmodus
            </button>
          )}
        </div>
        <label className="review-scope">
          <span className="muted small">Für</span>
          <select value={menu?.id ?? ""} onChange={(e) => pick(e.target.value)} data-testid="review-scope">
            <option value="">den ganzen Katalog</option>
            {data.menus.map((m) => (
              <option key={m.id} value={m.id}>
                die Speisekarte „{m.title || "ohne Titel"}“
              </option>
            ))}
          </select>
        </label>
        <TargetRows status={status} />
        <JobLine jobs={jobs} done={flow && step === 5} />
        {flow ? (
          <FlowBar step={step} status={status} />
        ) : (
          canJob && (
            <div className="buttons review-acts">
              {hasDe && deOpen > 0 && (
                <button onClick={() => void jobs.proofread(menu?.id ?? null)} disabled={jobs.proofing || jobs.translating} data-testid="review-proofread">
                  Deutsch prüfen ({deOpen}) · {centsText(proofreadEstimate(deOpen))}
                </button>
              )}
              {langs.length > 0 && (
                <button onClick={() => setPanel(!panel)} disabled={jobs.proofing || jobs.translating} aria-expanded={panel} data-testid="review-translate">
                  Übersetzen …
                </button>
              )}
              <button className="primary" onClick={() => [setFlow(true), setPanel(false), setQ(""), setGroup(null)]} data-testid="review-flow">
                Alles erledigen
              </button>
            </div>
          )
        )}
        {canJob && flow && step === 1 && !jobs.proofing && (
          <div className="flow-act" data-testid="flow-proofread">
            <p>
              {textsCount(deOpen)} noch nicht geprüft: Rechtschreibung und Typografie. Es kommen nur Vorschläge, nichts ändert sich von selbst.
            </p>
            <button className="primary" onClick={() => void jobs.proofread(menu?.id ?? null)} data-testid="flow-proofread-run">
              Deutsch prüfen ({deOpen}) · {centsText(proofreadEstimate(deOpen))}
            </button>
          </div>
        )}
        {canJob && ((flow && step === 3 && !jobs.translating) || (!flow && panel)) && <TranslatePanel key={`${flow}`} status={status} menu={menu?.id ?? null} jobs={jobs} warn={!flow ? deReview : 0} onReview={() => [setTargets(["de"]), setFilter("pruefen"), setPanel(false)]} onDone={() => setPanel(false)} />}
        {!flow && (
          <>
            <div className="chips lang-choice" role="group" aria-label="Zeigen" data-testid="review-targets">
              <span className="muted small">Zeigen</span>
              {status.targets.map((t) => (
                <button key={t} className={targets.includes(t) ? "active" : ""} aria-pressed={targets.includes(t)} onClick={() => toggle(t)} data-testid={`review-target-${t}`}>
                  {targetLabel(t)}
                </button>
              ))}
            </div>
            <div className="chips" data-testid="review-filters">
              {(
                [
                  ["pruefen", "durchsehen"],
                  ["offen", "offen"],
                  ["alle", "alle"],
                ] as const
              ).map(([k, label]) => (
                // Nothing in it: not to be chosen, unless it is the one chosen.
                <button key={k} className={filter === k ? "active" : ""} aria-pressed={filter === k} disabled={count(k) === 0 && filter !== k} onClick={() => [setFilter(k), settle(targets, k)]} data-testid={`review-filter-${k}`}>
                  {label} ({count(k)})
                </button>
              ))}
            </div>
            {groups.length > 1 && (
              <div className="chips review-groups" role="group" aria-label="Herkunft" data-testid="review-groups">
                {groups.map(([x, n]) => (
                  <button key={x} className={g === x ? "active" : ""} aria-pressed={g === x} onClick={() => setGroup(g === x ? null : x)} data-testid={`review-group-${x}`}>
                    {TEXT_GROUP_LABELS[x]} ({n})
                  </button>
                ))}
              </div>
            )}
            <div className="filters-row">
              <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suchen" aria-label="Texte suchen" />
            </div>
          </>
        )}
        {reviewable.length > 1 && editsMenus(s.role) && <AllSeen items={reviewable} flow={flow} />}
      </section>
      {flow && step === 5 ? (
        <Done status={status} data={data} menu={menu?.id ?? null} onExpert={() => setFlow(false)} />
      ) : rows.length === 0 ? (
        <section className="card">
          {/* Empty by a search, by a group worked through, or all done: said so, with the way out. */}
          {needle && status.rows.some((r) => inFilter(r, view, f) && (!g || r.group === g)) ? (
            <p data-testid="review-empty">
              Keine Treffer für „{q.trim()}“.{" "}
              <button className="link inline" onClick={() => setQ("")} data-testid="review-clear-search">
                Suche leeren
              </button>
            </p>
          ) : g ? (
            <p data-testid="review-empty">
              {TEXT_GROUP_LABELS[g]}: alles erledigt.{" "}
              <button className="link inline" onClick={() => setGroup(null)} data-testid="review-clear-group">
                Filter aufheben
              </button>
            </p>
          ) : (
            <p data-testid="review-empty">{f === "pruefen" ? "Alles durchgesehen." : f === "offen" ? "Nichts mehr offen." : "Noch nichts zu prüfen oder zu übersetzen."}</p>
          )}
          {menu && f === "pruefen" && !g && !needle && back}
        </section>
      ) : (
        GROUPS.map((g) => {
          const in_ = rows.filter((r) => r.group === g);
          if (!in_.length) return null;
          return (
            <section key={g} className="card">
              <h2>{TEXT_GROUP_LABELS[g]}</h2>
              <ul className="list review-list">
                {in_.map((r) => (
                  <ReviewRow key={r.id} row={r} targets={view} data={data} />
                ))}
              </ul>
            </section>
          );
        })
      )}
    </div>
  );
}

/**
 * The steps of „Fertig machen“ as main steps and their substeps (UI concept § 6a, Martin 02.11.2026):
 * an arrow for each main step – „Deutsch prüfen“ (have it checked, look through the proposals),
 * „Übersetzen“ (have it translated, look through the translations), „Fertig“ – and the line below
 * says which substep is due; the way into the expert mode stands beside the title.
 */
const MAIN_STEPS: { label: string; subs: readonly Step[] }[] = [
  { label: "Deutsch prüfen", subs: [1, 2] },
  { label: "Übersetzen", subs: [3, 4] },
];
const SUB_LABELS: Record<Exclude<Step, 5>, string> = { 1: "Prüfen lassen", 2: "Vorschläge durchsehen", 3: "Übersetzen lassen", 4: "Übersetzungen durchsehen" };

function FlowBar({ step, status }: { step: Step; status: TextStatus }) {
  const hasDe = status.targets.includes("de");
  const hasLangs = status.targets.some((t) => t !== "de");
  const mains = MAIN_STEPS.filter((m) => (m.subs[0] === 1 ? hasDe : hasLangs));
  const sub = step === 5 ? null : step;
  return (
    <div className="flow-bar" data-testid="flow-bar" data-substep={sub ? STEP_LABELS[sub] : "Fertig"}>
      <ol className="step-arrows">
        {mains.map((m) => {
          const done = step > m.subs[m.subs.length - 1]!;
          const now = m.subs.includes(step);
          return (
            <li key={m.label} className={done ? "done" : now ? "now" : ""} aria-current={now ? "step" : undefined}>
              {done ? "✓ " : ""}
              {m.label}
            </li>
          );
        })}
        {/* Reached, „Fertig“ is done like the steps before it: green with its tick (Martin, 02.11.2026). */}
        <li className={step === 5 ? "done" : ""} aria-current={step === 5 ? "step" : undefined}>
          {step === 5 ? "✓ " : ""}Fertig
        </li>
      </ol>
      {/* The substep due, in words (UI concept § 6a). */}
      {sub && (
        <p className="hint flow-sub" data-testid="flow-sub">
          <b>{SUB_LABELS[sub]}:</b> {sub === 1 && "Zuerst das Deutsche, damit kein Tippfehler in die Sprachen wandert."}
          {sub === 2 && (hasLangs ? "Jeden Vorschlag übernehmen oder mit „Nein“ ablehnen. Danach geht es mit dem Übersetzen weiter." : "Jeden Vorschlag übernehmen oder mit „Nein“ ablehnen.")}
          {sub === 3 && "Alle Sprachen in einem Gang. Danach siehst du durch, was der Übersetzer gemacht hat."}
          {sub === 4 && "Ändern oder „Passt“. Ändern geht auch später jederzeit."}
        </p>
      )}
    </div>
  );
}

/** What „Fertig machen“ ends with: what is done, the way back, the net when the card there changed. */
function Done({ status, data, menu, onExpert }: { status: TextStatus; data: Data; menu: string | null; onExpert: () => void }) {
  const s = useStore();
  const langs = status.targets.filter((t): t is Lang => t !== "de").map((l) => LANG_LABELS[l]);
  const said = [status.targets.includes("de") ? "Deutsch geprüft" : "", langs.length ? `${langs.length < 2 ? langs.join("") : `${langs.slice(0, -1).join(", ")} und ${langs.at(-1)}`} vollständig und durchgesehen` : ""].filter(Boolean).join(", ");
  return (
    <section className="card" data-testid="flow-done">
      <p className="ok-text">
        <b>Fertig:</b> {said}.
      </p>
      <NetBehind data={data} />
      {/* Two ways on, alike (UI concept § 6a); with nothing left to take over, the way back is the one to go. */}
      <p className="buttons">
        {menu && (
          <a className={`button ${data.published && outdated(data) && publishes(s.role) ? "" : "primary"}`} href={`#/speisekarte/${menu}?schritt=2`} data-testid="flow-back">
            ← Zurück zur Karte
          </a>
        )}
        {/* Later a translation changed by hand, a text looked at again (S61 § 11). */}
        <button onClick={onExpert} data-testid="flow-expert">
          Expertenmodus
        </button>
      </p>
    </section>
  );
}

/**
 * Before translating (S61, Martin: „Vorher die Zahl der Texte und Kosten zeigen, in der Seite“): the
 * languages, what each lacks, together and roughly what it costs and how long it takes. With corrections
 * to the German still open, a warning first (S61: the German first).
 */
function TranslatePanel({ status, menu, jobs, warn, onReview, onDone }: { status: TextStatus; menu: string | null; jobs: TextJobs; warn: number; onReview: () => void; onDone: () => void }) {
  const langs = status.targets.filter((t): t is Lang => t !== "de");
  const open = (l: Lang) => status.counts[l]?.open ?? { dishes: 0, texts: 0 };
  const [picked, setPicked] = useState<Lang[]>(() => langs.filter((l) => open(l).dishes + open(l).texts > 0));
  const work = picked.map(open);
  const dishes = work.reduce((n, c) => n + c.dishes, 0);
  const texts = work.reduce((n, c) => n + c.texts, 0);
  const cost = picked.reduce((n, l) => n + (translateEstimate(open(l).dishes, open(l).texts, 1) ?? 0), 0);
  const calls = picked.reduce((n, l) => n + Math.ceil(open(l).dishes / 30) + Math.ceil(open(l).texts / 80), 0);
  const minutes = Math.max(1, Math.round((calls * 25) / 60));
  const total = dishes + texts;
  return (
    <div className="flow-act" data-testid="translate-panel">
      {warn > 0 && (
        <p className="warn" data-testid="translate-warn">
          {warn === 1 ? "1 Text hat" : `${warn} Texte haben`} noch offene Vorschläge zum Deutschen; übersetzt würde der Text, wie er jetzt ist.{" "}
          <button className="link inline" onClick={onReview} data-testid="translate-review-first">
            Erst durchsehen
          </button>
        </p>
      )}
      <p>Übersetzen in</p>
      <ul className="translate-langs">
        {langs.map((l) => {
          const c = open(l);
          const n = c.dishes + c.texts;
          return (
            <li key={l}>
              <label className="check">
                <input type="checkbox" checked={picked.includes(l)} disabled={n === 0} onChange={(e) => setPicked(e.target.checked ? langs.filter((x) => x === l || picked.includes(x)) : picked.filter((x) => x !== l))} data-testid={`translate-pick-${l}`} />
                {LANG_LABELS[l]}: {n === 0 ? "nichts offen" : textsCount(n)}
              </label>
            </li>
          );
        })}
      </ul>
      <p className="hint" data-testid="translate-sum">
        {total === 0 ? "Nichts zu übersetzen." : `Zusammen ${total} ${total === 1 ? "Übersetzung" : "Übersetzungen"}, ${minutes === 1 ? "etwa eine Minute" : `etwa ${minutes} Minuten`}, ${centsText(cost)}. Was jemand von Hand übersetzt hat, bleibt.`}
      </p>
      <div className="buttons">
        <button
          className="primary"
          disabled={total === 0}
          onClick={() => {
            void jobs.translate(picked, menu);
            onDone();
          }}
          data-testid="translate-go"
        >
          {warn > 0 ? "Trotzdem übersetzen" : "Übersetzen"}
        </button>
      </div>
    </div>
  );
}

/** „Alle n sichtbaren als durchgesehen markieren“ (S61; a deed, not a finding: B3, 02.11.2026): with a second tap, so that it is meant. */
function AllSeen({ items, flow }: { items: { l: Lang; r: TextRow }[]; flow: boolean }) {
  const [sure, setSure] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function go() {
    setError(null);
    try {
      for (const l of [...new Set(items.map((x) => x.l))]) {
        const mine = items.filter((x) => x.l === l).map((x) => x.r);
        await checked(l, { dishes: mine.flatMap((r) => (r.dish ? [r.dish.id] : [])), texts: mine.flatMap((r) => (r.text ? [r.text] : [])) });
      }
      setSure(false);
    } catch (err) {
      setError(messageOf(err));
    }
  }
  return (
    <p className="all-seen" data-testid="all-seen">
      {sure ? (
        <>
          <span>{items.length} Übersetzungen vom Übersetzer als durchgesehen markieren?</span>{" "}
          <span className="buttons">
            <button className="primary" onClick={() => void go()} data-testid="all-seen-yes">
              Ja
            </button>
            <button onClick={() => setSure(false)} data-testid="all-seen-no">
              Nein
            </button>
          </span>
        </>
      ) : (
        // The main action of looking through in the flow; in the expert mode one way among others, „sichtbaren“ there since its filters decide.
        <button className={flow ? "primary" : ""} onClick={() => setSure(true)} data-testid="all-seen-ask">
          {flow ? `Alle ${items.length} als durchgesehen markieren` : `Alle ${items.length} sichtbaren als durchgesehen markieren`}
        </button>
      )}
      {error && <span className="error-text small"> {error}</span>}
    </p>
  );
}

/** Where a text stands, briefly: „Tapas-Karte · Abschnitt“, „Kategorie“. */
function whereOf(row: TextRow, data: Data): string {
  const titles = new Map(data.menus.map((m) => [m.id, m.title || "ohne Titel"]));
  const all = [...new Set(row.spots.map((s) => (s.key.startsWith("menu:") ? `${titles.get(s.key.split(":")[1]!) ?? "Karte"} · ${s.where}` : s.where)))];
  return all.length > 3 ? `${all.slice(0, 3).join(" · ")} …` : all.join(" · ");
}

/** One text with its German and its translations, each where it can be decided. */
function ReviewRow({ row, targets, data }: { row: TextRow; targets: readonly TextTarget[]; data: Data }) {
  const [error, setError] = useState<string | null>(null);
  const langs = targets.filter((t): t is Lang => t !== "de" && row.langs[t] !== undefined);
  const d = row.dish;
  return (
    <li className="review-row" data-testid="review-row" data-row={row.id}>
      <div className="review-de">
        <div className="review-what">
          {d ? (
            <>
              <span className="no">{d.number}</span> <b>{d.name}</b>
              {d.description && <span className="review-desc">{d.description}</span>}
            </>
          ) : (
            <>
              <b className="review-text">{row.text}</b>
              <span className="muted small">{whereOf(row, data)}</span>
            </>
          )}
        </div>
        {targets.includes("de") && row.de && <German row={row} data={data} onError={setError} />}
      </div>
      {langs.map((l) => (
        <div key={l} className="review-lang">
          {d ? <DishLang key={`${d.id}|${d.version}|${l}`} dish={d} lang={l} data={data} onError={setError} /> : <PhraseField key={`${row.text}|${l}|${data.version}`} text={row.text!} lang={l} data={data} onError={setError} long={row.text!.length > 70} />}
        </div>
      ))}
      {error && <p className="error-text small">{error}</p>}
    </li>
  );
}

/** The German of a row: its state, each correction once (also where the text stands in several places). */
function German({ row, data, onError }: { row: TextRow; data: Data; onError: (m: string) => void }) {
  const canEdit = row.dish || row.spots.some((s) => !s.key.startsWith("menu:")) ? editsCatalog(data.role) : editsMenus(data.role);
  const groups = new Map<string, { from: string; to: string; kind: ProofKind; items: { key: string; edit: string }[] }>();
  for (const s of row.spots) {
    for (const e of proofOf(s.text, data.proofs?.[s.key]).edits) {
      const k = `${e.from}\u0000${e.to}`;
      const g = groups.get(k) ?? { from: e.from, to: e.to, kind: e.kind, items: [] };
      g.items.push({ key: s.key, edit: e.id });
      groups.set(k, g);
    }
  }
  const all = [...groups.values()];
  async function take(items: { key: string; edit: string }[]) {
    try {
      await store.change(() => api("/api/proofread/take", { body: { items } }));
    } catch (err) {
      onError(messageOf(err));
    }
  }
  async function no(items: { key: string; edit: string }[]) {
    try {
      for (const it of items) await store.change(() => api("/api/proofread/reject", { body: it }));
    } catch (err) {
      onError(messageOf(err));
    }
  }
  if (!all.length) {
    return <span className={`tr-state ${row.de === "open" ? "missing" : ""}`}>{row.de === "open" ? "Deutsch ungeprüft" : "Deutsch geprüft ✓"}</span>;
  }
  const text = row.dish ? null : row.text!;
  return (
    <ul className="review-proofs" data-testid="review-proofs">
      {all.map((g) => {
        const where = row.dish ? (g.items.every((i) => i.key.endsWith(":name")) ? row.dish.name : row.dish.description) : text!;
        const diff = proofDiff(g.from, g.to);
        const at = where.indexOf(g.from);
        return (
          <li key={`${g.from}|${g.to}`} data-testid="review-proof">
            <span className="proof-text">
              {at > 0 && <span className="muted">…{where.slice(Math.max(0, at - 12), at)}</span>}
              {diff.before}
              <del>{shown(diff.out)}</del>
              <ins>{shown(diff.in)}</ins>
              {diff.after}
              <span className="muted small"> · {PROOF_KIND_LABELS[g.kind]}</span>
            </span>
            {canEdit && (
              <span className="proof-acts">
                <button type="button" onClick={() => void take(g.items)} data-testid="review-take">
                  Übernehmen
                </button>
                <button type="button" className="link" onClick={() => void no(g.items)} data-testid="review-reject">
                  Nein
                </button>
              </span>
            )}
          </li>
        );
      })}
      {all.length > 1 && canEdit && (
        <li>
          <button type="button" className="link" onClick={() => void take(all.flatMap((g) => g.items))} data-testid="review-take-all">
            Alle {all.length} übernehmen
          </button>
        </li>
      )}
    </ul>
  );
}

/** A dish's translation into one language: name (unless it stays) and description, its state, „Passt“. */
function DishLang({ dish, lang, data, onError }: { dish: Dish; lang: Lang; data: Data; onError: (m: string) => void }) {
  const canEdit = editsCatalog(data.role);
  const t = dish.translations?.[lang];
  const st = dishLangState(dish, lang);
  const nameRef = useRef<HTMLInputElement>(null);
  const descRef = useRef<HTMLTextAreaElement>(null);
  const keep = keepsName(dish);
  async function save(force = false) {
    const name = keep ? "" : (nameRef.current?.value ?? t?.name ?? "");
    const description = dish.description ? (descRef.current?.value ?? t?.description ?? "") : "";
    if (!force && name.trim() === (t?.name ?? "") && description.trim() === (t?.description ?? "")) return;
    try {
      await store.change(() => api(`/api/dishes/${dish.id}/translation`, { method: "PUT", body: { lang, name, description } }));
    } catch (err) {
      onError(messageOf(err));
    }
  }
  const word = st === "open" ? (t ? "unvollständig" : "fehlt") : st === "review" ? "nicht durchgesehen" : st === "stale" ? "⚠ Deutsch seitdem geändert" : "";
  return (
    <span className="tr-field dish-tr" data-testid={`tr-${lang}`}>
      <span className="lang-code" title={LANG_LABELS[lang]}>
        {LANG_SHORT[lang]}
      </span>
      <span className="dish-tr-fields">
        {!keep && <input ref={nameRef} defaultValue={t?.name ?? ""} placeholder={`Name auf ${LANG_LABELS[lang]}`} maxLength={TRANSLATION_LIMITS.name} disabled={!canEdit} onBlur={() => void save()} aria-label={`${dish.name}: Name auf ${LANG_LABELS[lang]}`} />}
        {dish.description && <textarea ref={descRef} rows={2} defaultValue={t?.description ?? ""} placeholder={`Beschreibung auf ${LANG_LABELS[lang]}`} maxLength={TRANSLATION_LIMITS.description} disabled={!canEdit} onBlur={() => void save()} aria-label={`${dish.name}: Beschreibung auf ${LANG_LABELS[lang]}`} />}
      </span>
      <span className={`tr-state ${st === "open" ? "missing" : ""}`}>
        {word}
        {canEdit && (st === "review" || st === "stale") && " "}
        {canEdit && st === "review" && (
          <button type="button" className="link inline" onClick={() => void checked(lang, { dishes: [dish.id] }).catch((err) => onError(messageOf(err)))} data-testid="tr-ok">
            Passt
          </button>
        )}
        {canEdit && st === "stale" && (
          <button type="button" className="link inline" onClick={() => void save(true)} data-testid="tr-ok">
            Passt so
          </button>
        )}
      </span>
    </span>
  );
}
